// ---------------------------------------------------------------------------
// InputsV3GlobalTopBar (V D do sandbox ``inputs_v3_topbar_variants.html``).
// ---------------------------------------------------------------------------
// Barra unica que consolida o que antes vivia espalhado entre a
// "monthlyStatus" inline (mes + refresh + fila) e o chip de status
// + botao "Cálculos" da ``ModelingProgressBandV3``. Ela e renderizada
// pela ``InputsV3.js`` num wrapper ``position: fixed; top: 0`` que
// "encaixa" no centro do header global da aplicacao.
//
// Layout (esquerda -> direita):
//
//   [dot + mes]    [icone + frase do estado]    [Entregues | Pendencias | Blocos]    [⟳]
//
// Decisoes editoriais (rodada de iteracao mai/2026):
//   - Status como frase unica qualificando agentes — sem badge.
//   - Os tres tiles sao clicaveis e abrem o ``ProgressDrawerV3`` na
//     aba correspondente:
//        * "Entregues"   -> aba "Linha do tempo" (lista de envios)
//        * "Pendencias"  -> aba "Pendencias" (matriz de acoes)
//        * "Calculos"    -> aba "Calculos" (blocos PnL)
//   - Refresh continua sendo um botao avulso (a unica acao
//     "global" que faz sentido no contexto desta barra). "Reset
//     mes" vive dentro do ``ProgressDrawerV3 > ADM > Ações``.
// ---------------------------------------------------------------------------

import React from "react";
import PropTypes from "prop-types";
import cx from "classnames";
import { useTranslation } from "react-i18next";
import { ThreeDots } from "react-loader-spinner";
import Tooltip from "@material-ui/core/Tooltip";
import Cached from "@material-ui/icons/Cached";
import Autorenew from "@material-ui/icons/Autorenew";
import CheckCircle from "@material-ui/icons/CheckCircle";
import ErrorOutline from "@material-ui/icons/ErrorOutline";
import HourglassEmpty from "@material-ui/icons/HourglassEmpty";
import CloudUpload from "@material-ui/icons/CloudUpload";

import useThemeColors from "../../../../hooks/useThemeColors";
import useStyles from "./styles";
import {
  monthStateLabel,
  formatActiveActorsLabel,
} from "../../../../services/inputsV3StateRules";

// Cor do spinner ``ThreeDots`` (cinza neutro, alinhada aos labels
// ``tileK`` / ``proseEm``). Mantemos hex direto para nao depender de
// ``INK_SOFT`` do ``styles.js`` (que e' privado ao modulo de estilos).
const SPINNER_COLOR = "#90a4ae";

// Mapa ``status -> variante visual``. Concentra aqui o conhecimento
// sobre quais estados sao "processing" vs "action" vs "ready" vs
// "waiting" para nao espalhar pelo render.
const VARIANT_BY_STATUS = {
  processing: "processing",
  running: "processing",
  verifying: "waiting",
  waiting: "waiting",
  unknown: "waiting",
  action_needed: "action",
  error: "action",
  system_error: "action",
  ready_to_calculate: "ready",
  stale: "ready",
  complete: "ready",
};

const ICON_BY_VARIANT = {
  processing: Autorenew,
  action: ErrorOutline,
  ready: CheckCircle,
  waiting: HourglassEmpty,
};

// ---------------------------------------------------------------------------
// Frase do estado (centro da barra).
// ---------------------------------------------------------------------------
// Retorna ``{ headline, complement }``. ``headline`` e a parte com
// peso ("Processando cruzamentos", "Ação necessária", "Pronto para
// cálculo"). ``complement`` e o que vem depois do separador (``·``)
// — atores em execucao, contadores, etc.
//
// Para os estados em curso (``processing``/``waiting``) preferimos a
// frase do ``formatActiveActorsLabel`` como complemento — ela traz
// os nomes dos inputs/xlinks/blocos realmente em execucao.
function buildStatusPhrase({ status, reason, active, pendingCount, t }) {
  const variant = VARIANT_BY_STATUS[status] || "waiting";
  const safeStatus = status || "waiting";
  // Headline — pega do bucket canonico (mesma fonte usada pelo chip
  // antigo). Quando o backend mandou o status, usamos o ``label``;
  // para um eventual nulo, caimos em "Aguardando inputs".
  //
  // O ``monthStateLabel`` traz a parte mais "verbal" da frase (em PT,
  // por enquanto — i18n dele e' um movimento maior, fora do escopo
  // desta barra).
  let headline = monthStateLabel(safeStatus, reason || null, t) || "—";

  // Complement — primeiro tentamos os atores em execucao (somente
  // em estados ativos). Sem eles, contextualizamos a frase com os
  // contadores ja exibidos nos tiles (sem duplicar conteudo:
  // valores aqui sao "humanizacoes" curtas).
  //
  // Para ``complete`` o complemento e' redundante com a headline
  // ("Concluído · cálculo concluído" — pedido do usuario para tirar).
  let complement = null;
  if (variant === "processing" || variant === "waiting") {
    complement = formatActiveActorsLabel(active, reason || null, t);
  }
  if (!complement) {
    if (safeStatus === "ready_to_calculate") {
      complement = t("inputs_v3.topbar.complement.all_delivered");
    } else if (safeStatus === "waiting" && reason === "crosslinks") {
      // Onda 5.x (2026-05-27): novo bucket ``waiting/crosslinks`` —
      // inputs todos entregues, mas cruzamentos ainda nao avaliados.
      // Mesma frase de complemento que ``ready_to_calculate``, com
      // headline diferente ("Aguardando cruzamentos") para deixar
      // claro o que falta antes do calculo.
      complement = t("inputs_v3.topbar.complement.all_delivered");
    } else if (safeStatus === "stale") {
      complement = t("inputs_v3.topbar.complement.recalc_needed");
    } else if (safeStatus === "action_needed" || safeStatus === "error") {
      if (pendingCount > 0) {
        complement = t("inputs_v3.topbar.complement.pending", {
          count: pendingCount,
        });
      }
    }
    // ``complete`` -> sem complement (redundante com a headline).
  }
  return { variant, headline, complement };
}

// ---------------------------------------------------------------------------
// Componente.
// ---------------------------------------------------------------------------

const InputsV3GlobalTopBar = ({
  monthLabel,
  selectedMonthStatusV3,
  selectedMonthStatusReasonV3,
  selectedMonthStatusActiveV3,
  delivered,
  total,
  pendingCount,
  blocksDone,
  blocksTotal,
  isRefreshing,
  refreshDisabled,
  onRefresh,
  onOpenPendings,
  onOpenBlocks,
  onOpenTimeline,
  transitionFaded,
  uploadingName,
  uploadProgress,
}) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const { clientColor } = useThemeColors();

  const { variant, headline, complement } = buildStatusPhrase({
    status: selectedMonthStatusV3,
    reason: selectedMonthStatusReasonV3,
    active: selectedMonthStatusActiveV3,
    pendingCount,
    t,
  });

  const ProseIcon = ICON_BY_VARIANT[variant] || HourglassEmpty;

  const leadDotCls = cx(classes.dot, {
    [classes.dotProcessing]: variant === "processing",
    [classes.dotAction]: variant === "action",
    [classes.dotReady]: variant === "ready",
    [classes.dotWaiting]: variant === "waiting",
  });
  const proseIconCls = cx(classes.proseIcon, {
    [classes.proseIconProcessing]: variant === "processing",
    [classes.proseIconAction]: variant === "action",
    [classes.proseIconReady]: variant === "ready",
    [classes.proseIconWaiting]: variant === "waiting",
  });
  const headlineCls = cx({
    [classes.proseLbl]: variant === "ready" || variant === "waiting",
    [classes.proseAccentInfo]: variant === "processing",
    [classes.proseAccentWarn]: variant === "action",
  });

  // Wrapper que aplica fade nos numeros dos tiles durante a
  // transicao entre meses. Mesma semantica do
  // ``ModelingProgressBandV3 > bottomLineFadeStyle`` (180ms).
  const numbersCls = transitionFaded
    ? classes.numbersFade
    : classes.numbersIdle;

  // Tiles ---------------------------------------------------------------
  // ``renderTile`` aceita tooltip = null para suprimir o popup
  // (caso do tile "Entregues" — sem tooltip, conforme pedido).
  //
  // Durante a transicao entre meses (``transitionFaded``) os tiles
  // ficam visualmente "fade" E logicamente nao-clicaveis. Isso
  // evita o usuario abrir o drawer enquanto o backend ainda esta
  // hidratando o novo mes — abrir nesse meio do caminho dispararia
  // o drawer com dados do mes anterior por uns ms.
  const renderTile = ({
    label,
    value,
    onClick,
    tooltip,
    warn,
    danger,
    divider,
  }) => {
    const clickable = typeof onClick === "function" && !transitionFaded;
    const cls = cx(classes.tile, {
      [classes.tileClickable]: clickable,
      [classes.tileDivider]: divider,
    });
    const nCls = cx(classes.tileN, numbersCls, {
      [classes.tileNWarn]: warn,
      [classes.tileNDanger]: danger,
    });
    const interactive = clickable ? (
      <button
        type="button"
        onClick={onClick}
        className={cls}
        aria-label={tooltip || label}
      >
        <span className={nCls}>{value}</span>
        <span className={classes.tileK}>{label}</span>
      </button>
    ) : (
      <div className={cls}>
        <span className={nCls}>{value}</span>
        <span className={classes.tileK}>{label}</span>
      </div>
    );
    // Tooltip suprimido enquanto faded — coerente com o
    // "nao-clicavel": ler "clique para detalhes" sem o clique
    // funcionar e' confuso.
    const showTooltip = Boolean(tooltip) && clickable;
    return showTooltip ? (
      <Tooltip title={tooltip} arrow placement="bottom">
        {interactive}
      </Tooltip>
    ) : (
      interactive
    );
  };

  const blocksValue =
    typeof blocksTotal === "number" && blocksTotal > 0
      ? `${blocksDone || 0}/${blocksTotal}`
      : "—";
  const pendingValue = typeof pendingCount === "number" ? pendingCount : 0;
  const hasTotal = typeof total === "number" && total > 0;
  const deliveredValue = hasTotal ? `${delivered || 0}/${total}` : "—";

  const clickForDetailsLabel = t("inputs_v3.topbar.click_for_details");
  const refreshLabel = t("inputs_v3.topbar.refresh");

  // Overlay de envio: ativo quando o pai passa o nome do input em
  // upload. ``uploadProgress`` vem do redux (``file_transfer.progress``,
  // 0..99 durante o multipart; reseta para 0 ao terminar).
  const isUploadingOverlay = Boolean(uploadingName);
  const uploadPct = Math.max(0, Math.min(100, Math.round(uploadProgress || 0)));

  // ---------------------------------------------------------------------

  return (
    <div className={classes.bar}>
      <div className={classes.lead}>
        <span className={leadDotCls} aria-hidden="true" />
        <span className={classes.month}>{monthLabel}</span>
      </div>

      <div className={classes.prose}>
        <span className={classes.proseWrap}>
          <span className={proseIconCls} aria-hidden="true">
            <ProseIcon />
          </span>
          <span>
            <span className={headlineCls}>{headline}</span>
            {complement && (
              <span className={classes.proseEm}> · {complement}</span>
            )}
          </span>
        </span>
      </div>

      <div className={classes.stats}>
        {renderTile({
          label: t("inputs_v3.topbar.tile_delivered"),
          value: deliveredValue,
          onClick: onOpenTimeline,
          tooltip: clickForDetailsLabel,
        })}
        {renderTile({
          label: t("inputs_v3.topbar.tile_pending"),
          value: pendingValue,
          warn: pendingValue > 0,
          onClick: onOpenPendings,
          tooltip: clickForDetailsLabel,
          divider: true,
        })}
        {renderTile({
          label: t("inputs_v3.topbar.tile_blocks"),
          value: blocksValue,
          onClick: onOpenBlocks,
          tooltip: clickForDetailsLabel,
          divider: true,
        })}
      </div>

      <div className={classes.actions}>
        <Tooltip title={refreshLabel} arrow placement="bottom">
          <span>
            <button
              type="button"
              className={cx(classes.iconBtn, {
                [classes.iconBtnBusy]: isRefreshing,
              })}
              onClick={isRefreshing ? undefined : onRefresh}
              disabled={refreshDisabled || isRefreshing}
              aria-label={refreshLabel}
            >
              <span className={classes.refreshSlot}>
                {isRefreshing ? (
                  // ``ThreeDots`` dentro de wrapper de tamanho fixo
                  // (18x18) — evita "saltinho" lateral quando o
                  // botao alterna entre icone e spinner. Cinza
                  // neutro para nao competir visualmente com o
                  // ``proseIcon`` (que ja narra o estado em curso).
                  <span className={classes.refreshDotsWrap}>
                    <ThreeDots
                      color={SPINNER_COLOR}
                      height={6}
                      width={18}
                      ariaLabel={refreshLabel}
                      visible
                    />
                  </span>
                ) : (
                  <Cached />
                )}
              </span>
            </button>
          </span>
        </Tooltip>
      </div>

      {isUploadingOverlay && (
        <div className={classes.uploadOverlay}>
          <span className={classes.uploadIcon} aria-hidden="true">
            <CloudUpload />
          </span>
          <span className={classes.uploadText}>
            {t("inputs_v3.topbar.uploading")} <strong>{uploadingName}</strong>…
          </span>
          <span className={classes.uploadPct}>{uploadPct}%</span>
          <div className={classes.uploadTrack}>
            <div
              className={classes.uploadFill}
              style={{ width: `${uploadPct}%`, background: clientColor }}
            />
          </div>
        </div>
      )}
    </div>
  );
};

InputsV3GlobalTopBar.propTypes = {
  monthLabel: PropTypes.node.isRequired,
  selectedMonthStatusV3: PropTypes.string,
  selectedMonthStatusReasonV3: PropTypes.string,
  selectedMonthStatusActiveV3: PropTypes.object,
  delivered: PropTypes.number,
  total: PropTypes.number,
  pendingCount: PropTypes.number,
  blocksDone: PropTypes.number,
  blocksTotal: PropTypes.number,
  isRefreshing: PropTypes.bool,
  refreshDisabled: PropTypes.bool,
  onRefresh: PropTypes.func,
  onOpenPendings: PropTypes.func,
  onOpenBlocks: PropTypes.func,
  onOpenTimeline: PropTypes.func,
  transitionFaded: PropTypes.bool,
  uploadingName: PropTypes.string,
  uploadProgress: PropTypes.number,
};

InputsV3GlobalTopBar.defaultProps = {
  selectedMonthStatusV3: null,
  selectedMonthStatusReasonV3: null,
  selectedMonthStatusActiveV3: null,
  delivered: 0,
  total: 0,
  pendingCount: 0,
  blocksDone: 0,
  blocksTotal: 0,
  isRefreshing: false,
  refreshDisabled: false,
  onRefresh: undefined,
  onOpenPendings: undefined,
  onOpenBlocks: undefined,
  onOpenTimeline: undefined,
  transitionFaded: false,
  uploadingName: null,
  uploadProgress: 0,
};

export default InputsV3GlobalTopBar;
